import { useFileComplaint } from "@complaints/complaintsQueries";
import { Button, Clipboard, Textarea } from "@scribenote/brian-design-system";
import { useEffect, useRef, useState } from "react";

/**
 * TODO(BE-1951): TEMPORARY, first-clinic launch only. Delete src/complaints/, the
 * CompanionControls hotkey branch, and the @complaints alias when it ends.
 *
 * Direct line to Scribenote (WEB-3201): a Ctrl/⌘+Shift+H popup where the practice
 * manager types what went wrong; submit fires brian's complaint endpoint, which
 * packages recent clinic context and alerts the team.
 *
 * On failure the typed message is kept so her words are never lost; success swaps
 * to a confirmation state rather than silently closing.
 */
export const ComplaintModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const complaint = useFileComplaint();

  // A native <dialog> stays mounted whether it is open or not, so nothing here
  // unmounts between openings: reset the draft and any prior send state each
  // time it opens, otherwise the last complaint's text or "Sent" screen flashes
  // back on reopen.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setMessage("");
  }
  const resetSend = complaint.reset;
  useEffect(() => {
    if (isOpen) resetSend();
  }, [isOpen, resetSend]);

  // showModal() picks the focus target itself, so claim it back one frame later
  // and put the caret in the field she came here to type in.
  useEffect(() => {
    if (!isOpen) return;
    const id = requestAnimationFrame(() => textareaRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  const canSend = message.trim().length > 0 && !complaint.isPending;
  const send = () => {
    if (canSend) complaint.mutate(message.trim());
  };

  return (
    <Clipboard
      open={isOpen}
      onClose={onClose}
      // Dropped while closed: a native <dialog> stays mounted, so an
      // unconditional testid would report an open form that isn't there.
      data-testid={isOpen ? "complaint-modal" : undefined}
      title="Report a problem"
      footer={
        complaint.isSuccess ? (
          <Button onClick={onClose} data-testid="complaint-done">
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={onClose}
              data-testid="complaint-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={send}
              disabled={!canSend}
              busy={complaint.isPending}
              data-testid="complaint-send"
            >
              {complaint.isPending ? "Sending" : "Send to Scribenote"}
            </Button>
          </>
        )
      }
    >
      {complaint.isSuccess ? (
        <p className="text-sm text-ink-body" data-testid="complaint-sent">
          Sent. The Scribenote team has been notified.
        </p>
      ) : (
        <div className="mx-auto flex max-w-[38rem] flex-col gap-3">
          <p className="text-sm text-annotation">
            Goes straight to the Scribenote team with recent activity attached.
          </p>
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="What went wrong? What were you trying to do?"
            minRows={4}
            maxRows={12}
            disabled={complaint.isPending}
            error={
              complaint.isError
                ? "Couldn't send, and your text is kept. Try again, or text the Scribenote team directly."
                : undefined
            }
            data-testid="complaint-textarea"
          />
        </div>
      )}
    </Clipboard>
  );
};
